export type ApiProblem = {
  status?: number
  code?: string
  message: string
  details?: unknown
}

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function nonblank(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

export function parseApiProblem(error: unknown, fallback: string): ApiProblem {
  const errorRecord = isRecord(error) ? error : undefined
  const status =
    typeof errorRecord?.status === 'number' ? errorRecord.status : undefined
  const problem = isRecord(errorRecord?.problem)
    ? errorRecord.problem
    : undefined
  const detail = problem?.detail

  if (isRecord(detail)) {
    const message = nonblank(detail.message)
    if (message) {
      return {
        ...(status === undefined ? {} : { status }),
        ...(nonblank(detail.code) ? { code: nonblank(detail.code) } : {}),
        message,
        ...('details' in detail ? { details: detail.details } : {}),
      }
    }
  }

  const stringDetail = nonblank(detail)
  if (stringDetail) {
    return {
      ...(status === undefined ? {} : { status }),
      message: stringDetail,
    }
  }

  const title = nonblank(problem?.title)
  if (title) {
    return {
      ...(status === undefined ? {} : { status }),
      message: title,
    }
  }

  const errorMessage = error instanceof Error ? nonblank(error.message) : undefined
  const safeErrorMessage =
    errorMessage && errorMessage !== '[object Object]' ? errorMessage : undefined
  const message = safeErrorMessage ?? (status === undefined ? fallback : `HTTP ${status}`)
  return {
    ...(status === undefined ? {} : { status }),
    message,
  }
}

/**
 * Turn a refused `fetch` response into a thrown error that `parseApiProblem`
 * can read — status plus the server's own body — instead of a bare "HTTP 409"
 * that tells the operator nothing.
 */
export async function failFromResponse(res: Response): Promise<never> {
  let problem: unknown
  try {
    problem = await res.json()
  } catch {
    problem = undefined
  }
  throw Object.assign(new Error(`HTTP ${res.status}`), { status: res.status, problem })
}

/** Fields a refused `fetch` response carried, read once from its JSON body. */
export type ResponseError = {
  status: number
  /**
   * The server's own human message — `detail` (string), `detail.message`,
   * the `{ error: { message } }` envelope, then the problem `title` — or
   * `null` when the body carried none of them.
   */
  message: string | null
  code: string | null
  details: unknown
}

/**
 * Read a refused `fetch` response into its parts so a module-specific error
 * class (with `code` / `details`) can be built without re-parsing the body.
 */
export async function readResponseError(res: Response): Promise<ResponseError> {
  const payload: unknown = await res.json().catch(() => null)
  const body = isRecord(payload) ? payload : undefined
  const detail = body?.detail
  const detailRecord = isRecord(detail) && !Array.isArray(detail) ? detail : undefined
  const errorField = body?.error
  const envelope = isRecord(errorField) ? errorField : undefined

  const message =
    nonblank(detail) ??
    nonblank(detailRecord?.message) ??
    nonblank(envelope?.message) ??
    nonblank(body?.title) ??
    null
  const code = nonblank(detailRecord?.code) ?? nonblank(envelope?.code) ?? null

  return {
    status: res.status,
    message,
    code,
    details: detailRecord?.details,
  }
}

/** Default text when the server sent no message: `HTTP 500`. */
export const httpStatusFallback = (status: number): string => `HTTP ${status}`

/** Older wording some modules (and their tests) keep: `Request failed: 500`. */
export const requestFailedFallback = (status: number): string =>
  `Request failed: ${status}`

/**
 * The human message of a refused `fetch` response, or `fallback(status)` when
 * the body carried none. Modules that historically said
 * `Request failed: 500` pass their own fallback to keep that wording.
 */
export async function readErrorMessage(
  res: Response,
  fallback: (status: number) => string = httpStatusFallback,
): Promise<string> {
  const { status, message } = await readResponseError(res)
  return message ?? fallback(status)
}
